<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center flex-wrap">
      <span v-t="'accommodationHistory'" />
      <v-btn-toggle v-model="groupFilter" mandatory dense color="primary" class="occupancy-group-toggle" @change="fetchHistory">
        <v-btn small value="all" v-t="'allOccupancy'" />
        <v-btn small value="current" v-t="'currentOccupancy'" />
        <v-btn small value="future" v-t="'futureOccupancy'" />
        <v-btn small value="past" v-t="'pastOccupancy'" />
      </v-btn-toggle>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :group-by="groupFilter === 'all' ? 'group' : null"
      sort-by="dateStart"
      sort-desc
      item-key="id"
      :items-per-page="-1"
      hide-default-footer
      disable-pagination
      :mobile-breakpoint="0"
    >
      <template #[`item.employeeName`]="{ item }">
        <router-link
          v-if="item.employeeId"
          :to="{ name: 'staff-employee-view', params: { id: item.employeeId } }"
          class="text-decoration-none"
        >
          {{ item.employeeName || '—' }}
        </router-link>
        <span v-else-if="item.occupied" class="text--secondary">
          {{ item.note || $t('occupied') }}
        </span>
        <span v-else class="text--secondary font-italic">
          {{ item.employeeName || '—' }}
        </span>
      </template>

      <template #[`item.dateStart`]="{ item }">
        {{ item.dateStart || '—' }}
      </template>

      <template #[`item.dateEnd`]="{ item }">
        {{ item.dateEnd || $t('indefinitePeriod') }}
      </template>

      <template #[`item.createdByName`]="{ item }">
        {{ item.createdByName || '—' }}
      </template>

      <template #[`item.closedByName`]="{ item }">
        {{ item.closedByName || '—' }}
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip v-if="item.group !== 'past'" bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              data-testid="history-check-out"
              v-bind="attrs"
              v-on="on"
              @click="askCheckOut(item)"
            >
              <v-icon size="18">{{ icons.mdiLogoutVariant }}</v-icon>
            </v-btn>
          </template>
          <span v-t="'checkOut'" />
        </v-tooltip>
        <span v-else>—</span>
      </template>

      <template #[`group.header`]="{ group, headers: hdrs, toggle, isOpen }">
        <td :colspan="hdrs.length" class="grey lighten-4">
          <v-btn icon small @click="toggle">
            <v-icon>{{ isOpen ? icons.mdiChevronDown : icons.mdiChevronRight }}</v-icon>
          </v-btn>
          <span v-if="group === 'current'" v-t="'currentOccupancy'" class="font-weight-semibold ms-2" />
          <span v-else-if="group === 'future'" v-t="'futureOccupancy'" class="font-weight-semibold ms-2" />
          <span v-else-if="group === 'past'" v-t="'pastOccupancy'" class="font-weight-semibold ms-2" />
        </td>
      </template>

      <template #no-data>
        <span class="text--secondary">{{ $t('noData') }}</span>
      </template>
    </v-data-table>

    <v-dialog v-model="isCheckOutDialogVisible" max-width="650px">
      <v-card class="pa-sm-10 pa-3">
        <v-card-title v-t="'checkOutOccupantTitle'" class="justify-center text-h5" />
        <v-card-text>
          <v-row>
            <v-col v-t="'checkOutOccupantConfirm'" cols="12" class="d-flex align-center justify-center text-center" />
          </v-row>
          <v-row>
            <v-col cols="12" class="d-flex justify-center mt-3">
              <v-btn v-t="'discard'" outlined class="me-3" :disabled="checkOutLoading" @click="discardCheckOut" />
              <v-btn :loading="checkOutLoading" color="primary" @click="confirmCheckOut">{{ $t('checkOut') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mdiChevronDown, mdiChevronRight, mdiLogoutVariant } from '@mdi/js'
import { ref, onMounted, computed, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'

export default {
  setup() {
    const vm = getCurrentInstance().proxy
    const { getRoomOccupancyHistory, checkOutOccupant } = useActions('staff', [
      'getRoomOccupancyHistory',
      'checkOutOccupant',
    ])
    const { roomData } = useState('staff', ['roomData'])

    const items = ref([])
    const loading = ref(false)
    const groupFilter = ref('current')

    const headers = computed(() => [
      { text: vm.$t('employee'), value: 'employeeName', sortable: true, groupable: false },
      { text: vm.$t('accommodationDateStart'), value: 'dateStart', sortable: true, groupable: false },
      { text: vm.$t('accommodationDateEnd'), value: 'dateEnd', sortable: true, groupable: false },
      { text: vm.$t('checkedInBy'), value: 'createdByName', sortable: false, groupable: false },
      { text: vm.$t('checkedOutBy'), value: 'closedByName', sortable: false, groupable: false },
      { text: vm.$t('note'), value: 'note', sortable: false, groupable: false },
      { text: vm.$t('actions'), value: 'actions', sortable: false, groupable: false, align: 'center' },
    ])

    const fetchHistory = async () => {
      if (!roomData.value || !roomData.value.id) return
      loading.value = true
      const params = { page_size: 200 }
      if (groupFilter.value !== 'all') params.group = groupFilter.value
      const { data, success } = await getRoomOccupancyHistory({
        roomId: roomData.value.id,
        params,
      })
      if (success) {
        items.value = (data && data.results) || data || []
      }
      loading.value = false
    }

    const isCheckOutDialogVisible = ref(false)
    const checkOutLoading = ref(false)
    const itemToCheckOut = ref(null)

    const askCheckOut = (item) => {
      itemToCheckOut.value = item
      isCheckOutDialogVisible.value = true
    }
    const discardCheckOut = () => {
      itemToCheckOut.value = null
      isCheckOutDialogVisible.value = false
    }
    const confirmCheckOut = async () => {
      if (!itemToCheckOut.value) return
      checkOutLoading.value = true
      const { success } = await checkOutOccupant({ occupantId: itemToCheckOut.value.id })
      if (success) await fetchHistory()
      checkOutLoading.value = false
      isCheckOutDialogVisible.value = false
      itemToCheckOut.value = null
    }

    onMounted(() => fetchHistory())
    watch(() => roomData.value && roomData.value.id, () => fetchHistory())

    return {
      headers,
      items,
      loading,
      groupFilter,
      fetchHistory,
      isCheckOutDialogVisible,
      checkOutLoading,
      askCheckOut,
      discardCheckOut,
      confirmCheckOut,
      icons: { mdiChevronDown, mdiChevronRight, mdiLogoutVariant },
    }
  },
}
</script>

<style lang="scss" scoped>
.occupancy-group-toggle {
  &.v-btn-toggle {
    gap: 4px;
  }
  ::v-deep .v-btn {
    border-radius: 4px !important;
    border-left-width: thin !important;
  }
}
</style>
